import { Form } from "@circle-react-uikit/Form";
import {
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { communityLinkInvalidations } from "@circle-react/lib/queries/communityLinks";
import { Modal } from "@circleco/compass/components/Modal";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import type { ApiError } from "@/react/config/CustomErrors";

import type { SidebarLink } from "../LinkItem";
import { AddLinkForm } from "./AddLinkForm";
import { ADD_LINK_FORM_ID, ADD_LINK_VALIDATION_SCHEMA } from "./constants";

interface AddLinkFormValues {
  may_lead_to_purchase?: boolean;
  name: string;
  url: string;
}

export interface AddLinkModalComponentProps {
  isEdit?: boolean;
  link?: SidebarLink;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const AddLinkModalComponent = ({
  isEdit = false,
  link,
  onOpenChange,
  open,
}: AddLinkModalComponentProps) => {
  const afterCreateOrUpdate = () => {
    void communityLinkInvalidations.all();
    onOpenChange(false);
  };

  const { mutate: createLink, isPending: isSubmitting } = useMutation<
    unknown,
    ApiError,
    AddLinkFormValues
  >({
    mutationFn: data =>
      reactQueryPost(internalApi.communities.communityLinks.create(), data),
    onSuccess: afterCreateOrUpdate,
  });

  const { mutate: updateLink, isPending: isUpdating } = useMutation<
    unknown,
    ApiError,
    AddLinkFormValues
  >({
    mutationFn: data =>
      reactQueryPut(
        internalApi.communities.communityLinks.update({
          communityLinkId: link?.id.toString(),
        }),
        data,
      ),
    onSuccess: afterCreateOrUpdate,
  });

  const isPending = isSubmitting || isUpdating;

  const submitLabel = (() => {
    if (isEdit) {
      return isUpdating ? t("link_updating") : t("link_update");
    }

    return isSubmitting ? t("link_creating") : t("link_create");
  })();

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header
          title={isEdit ? t("edit_link") : t("add_link")}
          showCloseButton
        />
        <Modal.Body>
          <Form<AddLinkFormValues>
            id={ADD_LINK_FORM_ID}
            defaultValues={
              isEdit && link
                ? {
                    name: link.name,
                    url: link.url,
                    may_lead_to_purchase: link.may_lead_to_purchase,
                  }
                : { name: "", url: "", may_lead_to_purchase: false }
            }
            validationSchema={ADD_LINK_VALIDATION_SCHEMA}
            onSubmit={data => {
              if (isEdit) {
                updateLink(data);
              } else {
                createLink(data);
              }
            }}
          >
            <AddLinkForm />
          </Form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{ label: t("cancel") }}
          primaryAction={{
            label: submitLabel,
            type: "submit",
            form: ADD_LINK_FORM_ID,
            loading: isPending,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
